import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import GenericBasicInfo from "../../../../../components/cards/GenericBasicInfo";
import {
  cardStyles,
  modalGenericStyles,
  variables,
} from "../../../../../variables";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  patchFerryReservation,
  retrieveFerryReservation,
} from "../../../../../api/CRS/Ferries";
import { Price } from "../../../../../components/Price";
import { DateTime } from "luxon";
import GuestCard from "../../../../../components/cards/GuestInfo";
import useReservationDetails from "../../../../../hooks";
import EditGuestModal from "../../../CommonCRSModals/EditGuestModal";
import EmailsCard from "../../../../../components/cards/emails";
import Loader from "../../../../../components/Loader";
import EditCRSTagModal from "../../../CommonCRSModals/EditCRSTagModal/index";
import { toast } from "react-toastify";
import ReservationEventsModal from "../../../ReservationEventsModal";
import { DocumentsCRSCard } from "../../../CommonCRSModals/DocumentsCRS/List";
import ReservationsStatusTag from "../../../../../components/Tags/ReservationsStatusTag";
import TabStrip from "../../../../../components/TabStrip";
import { CustomButton } from "../../../../../components/CustomButton";
import FERLogsTab from "./FERLogsTab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { genericRequestErrorHandler } from "../../../../../api";
import Receivables from "../../../CommonCRSModals/Receivables";
import { InternalLogs } from "../../../CommonCRSModals/InternalLogs";

const styles = createUseStyles({
  ...modalGenericStyles,
  DetailFerryModals: {
    ...modalGenericStyles.modal,
  },
  modalCard: {
    ...modalGenericStyles.card,
    gridTemplateRows: "max-content max-content 1fr max-content",
  },
  modalCardHeader: {
    ...modalGenericStyles.cardHeader,
  },
  modalCardBody: {
    ...modalGenericStyles.cardBody,
    display: "grid",
    padding: "",
    overflowX: "hidden",
  },
  modalCardActions: {
    ...modalGenericStyles.cardActions,
  },
  card: { ...cardStyles.card, maxWidth: "100%" },
  cardHeader: cardStyles.header,
  cardBody: {
    ...cardStyles.body,
    gap: variables.normal_gap,
    maxWidth: "100%",
    overflow: "auto",
    justifyContent: "start",
  },
  firstRow: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    padding: variables.normal_gap,
    gap: variables.normal_gap,
  },
  creatorCardBody: {
    extend: "cardBody",
    padding: [`calc(${variables.normal_gap} * 3.3)`, variables.normal_gap],
  },
  componentRow: {
    extend: "firstRow",
    gridTemplateColumns: "repeat(1, 1fr)",
  },
  [`@media ${variables.media.smallscreen}`]: {
    modalCardBody: {
      minWidth: "60rem",
    },
    firstRow: {
      gridTemplateColumns: "1fr 1fr",
      justifyItems: "center",
    },
  },
});
const DetailFerryModals = ({ reference, onClose }) => {
  const classes = styles();

  const [activeTab, setActiveTab] = useState("basicInfo");

  const queryClient = useQueryClient();

  const menuTabs = {
    basicInfo: "Basic Info",
    reservationEvents: "Reservation Events",
    Receivables: "Receivables",
    documents: "Documents",
    internalLogs: "Internal Logs",
    logs: "User Action Logs",
  };

  const {
    data: ferry,
    guestEditMode,
    guestEditId,
    editCRSTagMode,
    reservationEventsModal,
    openGuestModal,
    closeGuestModal,
    openEditCRSTagModal,
    closeEditCRSTagModal,
    closeReservationEventsModal,
    // loading,
  } = useReservationDetails({
    reference,
    retrieveReservationData: retrieveFerryReservation,
    onCloseDetails: onClose,
  });

  const basicDataCpl = [
    ["Reference", "reference"],
    ["Supplier Reference", "supplier_reference"],
    ["Company", "company"],
    ["Destination", "destination"],
    ["Adults", "adults"],
    ["Children", null, _.get(ferry, "data.children", "0")],
    [
      "Arrival",
      null,
      DateTime.fromISO(_.get(ferry, "data.arrival")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    [
      "Original Service Price",
      null,
      <Price
        currency={_.get(ferry, "data.currency")}
        price={_.get(ferry, "data.original_service_price")}
      />,
    ],
    ["Payment Mode", "payment_mode_display"],
    ["Ticket Class", "ticket_class"],
    ["Market Type", "market_type"],
    ["Source Entity", "source_entity"],
    [
      "Created",
      null,
      DateTime.fromISO(_.get(ferry, "data.created")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    [
      "Status",
      null,
      <ReservationsStatusTag status={_.get(ferry, "data.status")} />,
    ],
  ];
  const creatorDataCpl = [
    ["External Creator", "external_creator"],
    [
      "Creator",
      null,
      _.get(ferry, "data.guests", []).map((des) => _.get(des, "creator")),
    ],
  ];

  const { mutate: onSubmitTags, isLoading: tagsLoading } = useMutation({
    mutationFn: (values) => {
      return patchFerryReservation({ payload: values, reference });
    },
    onSuccess: () => {
      toast.success("Tags updated successfully");
      queryClient.invalidateQueries("ferries");
      onClose();
    },
    onError: (error) => {
      genericRequestErrorHandler(error);
    },
  });

  return (
    <div id="DetailFERModal" className={classes.DetailFerryModals}>
      <div className={classes.modalCard}>
        <div className={classes.modalCardHeader}>
          <span>Ferry Details</span>
        </div>
        <TabStrip
          tabs={Object.entries(menuTabs)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <div className={classes.modalCardBody}>
          {_.isEmpty(ferry) ? (
            <Loader />
          ) : (
            <React.Fragment>
              {activeTab === "basicInfo" && (
                <React.Fragment>
                  <div className={classes.firstRow}>
                    <div className={classes.basicInfo}>
                      <GenericBasicInfo
                        actions={[["Edit", () => openEditCRSTagModal()]]}
                        header={"Basic Data"}
                        cardInfo={basicDataCpl}
                        objectData={_.get(ferry, "data")}
                      />
                    </div>
                    <div className={classes.creatorCardBody}>
                      <GenericBasicInfo
                        header="Creator Data"
                        cardInfo={creatorDataCpl}
                        objectData={_.get(ferry, "data")}
                      />
                    </div>
                  </div>
                  <div className={classes.componentRow}>
                    <div className={classes.cardBody}>
                      <GuestCard
                        title={"Guest Informations"}
                        guests={_.get(ferry, "data.guests", [])}
                        onEditGuest={(id) => {
                          openGuestModal(id);
                        }}
                        onCloseEdit={() => closeGuestModal()}
                      />
                      <EmailsCard
                        title={"Email Informations"}
                        market_type={_.get(ferry, "data.market_type")}
                        reservation_id={_.get(ferry, "data.reference")}
                        service_type="FER"
                        status={_.get(ferry, "data.status")}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )}
              {activeTab === "reservationEvents" && (
                <ReservationEventsModal
                  header="Reservation Events"
                  onClose={() => closeReservationEventsModal()}
                  events={_.get(ferry, "data.reservation_events", [])}
                />
              )}
              {guestEditMode && !reservationEventsModal && !editCRSTagMode && (
                <EditGuestModal
                  onClose={closeGuestModal}
                  guestId={guestEditId}
                  queryKey={`ferry_reservation_users_documents`}
                  reference={_.get(ferry, "data.reference")}
                  sourceEntity={_.get(ferry, "data.source_entity", "")}
                />
              )}
              {!guestEditMode && !reservationEventsModal && editCRSTagMode && (
                <EditCRSTagModal
                  header={"Ferry"}
                  tagsLoading={tagsLoading}
                  tags={_.get(ferry, "data.tags")}
                  onClose={closeEditCRSTagModal}
                  onSubmitTags={onSubmitTags}
                />
              )}
              {activeTab === "receivables" && (
                <Receivables data={_.get(ferry, "data.payment_links", [])} />
              )}
              {activeTab === "documents" && (
                <DocumentsCRSCard
                  title="Documents"
                  queryKey={`ferries_reservation_document`}
                  showAddButton={true}
                  tags={[`${_.get(ferry, "data.reference")}_reservation_FER`]}
                  reference={_.get(ferry, "data.reference")}
                  sourceEntity={_.get(ferry, "data.source_entity", "")}
                />
              )}
              {activeTab === "internalLogs" && (
                <InternalLogs
                  prebookData={_.get(ferry, "data.prebook_data", {})}
                  bookData={_.get(ferry, "data.post_booking_data", {})}
                />
              )}
              {activeTab === "logs" && <FERLogsTab />}
            </React.Fragment>
          )}
        </div>
        <div className={classes.cardActions}>
          <CustomButton id="cancel" appearance="ghost" onClick={onClose}>
            <strong>Cancel</strong>
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
DetailFerryModals.propTypes = {
  onClose: PropTypes.func.isRequired,
};
export default DetailFerryModals;
